<script setup lang="ts">
import { computed, ref } from 'vue';
import { OBJECT } from '@pkg/config/sanity/schemas';
import { AnalyticsClickBehaviour } from '@pkg/config/misc/analyticsClickBehaviour';
import { logger } from '@pkg/logger';
import type { InternalLink, PageReference } from '@pkg/sanity-utils/linkQueries';
import { isPageReference } from '../lib';
import {
  buildAbsoluteSiteUrl,
  isBusinessCrossSiteTarget,
  isWwwCrossSiteTarget,
  normalizePublicOrigin,
} from '../lib/crossSiteLink';
import type { LinkType } from '../types';
import { formatSlug } from '@pkg/sanity-utils/slugs';
import { useSingleSignUpParameters } from '@pkg/analytics-helper/src/utilities/useSingleSignUpParameter';
import { getSanitizedNavigationQueryAndHash } from '@pkg/utilities/urls/sanitizeNavigationQuery';
import { useRoute } from 'vue-router';

defineOptions({
  inheritAttrs: false
});

interface Props {
  link: LinkType;
  title?: string;
  buttonClickCallback?: Function;
  newWindow?: boolean;
  anchor?: string;
  showSeparator?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showSeparator: false
});

const { locale } = useI18n();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const localePath = useLocalePath();
let updatedParams = ref('');

const publicConfig = runtimeConfig.public as {
  siteId?: string;
  wwwDomain?: string;
  businessDomain?: string;
  paramsAllowList?: string;
};

const eventHandler = () => {
  if (typeof props.buttonClickCallback === 'function') {
    props.buttonClickCallback();
  }
};


const getExternalTarget = () => {
  if (props.link._type === OBJECT.NAVIGATION_LINK_SSU) {
    return '_blank';
  } else if (props.link._type === OBJECT.NAVIGATION_LINK_EXTERNAL) {
    return props.link.newWindow ? '_blank' : '_self';
  } else {
    return props.newWindow ? '_blank' : '_self';
  }
};

const getInternalLinkTo = (link: InternalLink | PageReference, anchor: string = '') => {
  const reference = link._type === OBJECT.NAVIGATION_LINK_INTERNAL ? link.reference : link;
  
  if (reference == null) {
    if (link._type === OBJECT.NAVIGATION_LINK_INTERNAL && link.internalAnchor) {
      // We're happy to show internal anchor links on the same page
      return handleAnchor(link.internalAnchor);
    }

    logger.error('Link reference is null', link);
    return;
  }

  const slug = formatSlug(reference.slug ?? '/');

  const { query, hash: preservedHash } = getSanitizedNavigationQueryAndHash(
    route.query,
    route.hash,
    publicConfig.paramsAllowList,
  );
  const explicitHash =
    anchor || link.internalAnchor
      ? handleAnchor(anchor || link.internalAnchor)
      : null;
  const hash =
    explicitHash && explicitHash !== ''
      ? explicitHash
      : preservedHash;

  const path = localePath(slug, reference.locale);
  const audienceRef = 'audienceRef' in reference ? reference.audienceRef : undefined;
  const documentType = reference._type;

  if (isWwwCrossSiteTarget(publicConfig.siteId, audienceRef, documentType)) {
    const wwwOrigin = normalizePublicOrigin(publicConfig.wwwDomain);
    if (!wwwOrigin) {
      logger.warn(
        'Cross-site link to www requires NUXT_PUBLIC_WWW_DOMAIN; falling back to same-origin path',
        { slug, audienceRef, documentType, siteId: publicConfig.siteId },
      );
    } else {
      return buildAbsoluteSiteUrl(wwwOrigin, path, query, hash);
    }
  }

  if (isBusinessCrossSiteTarget(
    publicConfig.siteId,
    audienceRef,
    documentType,
  )) {
    const businessOrigin = normalizePublicOrigin(publicConfig.businessDomain);
    if (!businessOrigin) {
      logger.warn(
        'Cross-site link to business requires NUXT_PUBLIC_BUSINESS_DOMAIN; falling back to same-origin path',
        { slug, audienceRef, documentType, siteId: publicConfig.siteId },
      );
    } else {
      return buildAbsoluteSiteUrl(businessOrigin, path, query, hash);
    }
  }

  return {
    path,
    ...(Object.keys(query).length > 0 ? { query } : {}),
    ...(hash ? { hash } : {}),
  };
};

const isExternalLink = computed(() => {
  return props.link &&
      (props.link._type === OBJECT.NAVIGATION_LINK_EXTERNAL ||
          props.link._type === OBJECT.NAVIGATION_LINK_SOCIAL ||
          props.link._type === OBJECT.NAVIGATION_LINK_SSU);
});


const handleAnchor = (anchor: string | undefined) => anchor && anchor.startsWith('#') ? '' : `#${anchor}`;

const updateMarketingParameters = () => {
  updatedParams.value = useSingleSignUpParameters(locale.value);
};
</script>

<template>
  <NuxtLink
    v-if="props.link && props.link._type === OBJECT.NAVIGATION_LINK_INTERNAL"
    v-show="props.link.reference != null || props.link.internalAnchor != null"
    :to="getInternalLinkTo(props.link)"
    :target="getExternalTarget()"
    :title="props.title ?? props.link.title"
    :data-analytics-click-behaviour="AnalyticsClickBehaviour.InternalLink"
    class="amp-auto-track"
    v-bind="$attrs"
    @click="eventHandler"
  >
      <slot>
        <p class="c-custom-link__text">{{ props.title ?? props.link.title }}</p>
      </slot>
    </NuxtLink>
    <NuxtLink
      v-else-if="props.link && isPageReference(props.link)"
      :to="getInternalLinkTo(props.link, props.anchor)"
      :target="getExternalTarget()"
      :title="props.title"
      :data-analytics-click-behaviour="AnalyticsClickBehaviour.InternalLink"
      class="amp-auto-track"
      v-bind="$attrs"
      @click="eventHandler"
    >
      <slot>
        <p class="c-custom-link__text">{{ props.title }}</p>
      </slot>
    </NuxtLink>
    <NuxtLink
      v-else-if="isExternalLink"
      :to="`${props.link.url}${updatedParams}`"
      :target="getExternalTarget()"
      :title="props.title ?? props.link.title"
      v-bind="$attrs"
      :data-analytics-click-behaviour="AnalyticsClickBehaviour.SSU"
      :rel="props.link._type === OBJECT.NAVIGATION_LINK_SSU ? '' : undefined"
      @click="eventHandler"
      @mouseover="updateMarketingParameters"
      @mousedown="updateMarketingParameters"
    >
      <slot>
        <p class="c-custom-link__text">{{ props.title ?? props.link.title }}</p>
      </slot>
    </NuxtLink>
    <span v-if="showSeparator" class="c-custom-link__separator" aria-hidden="true">|</span>
</template>

<style scoped>
.c-custom-link__separator {
  margin-inline: 0.1em;
  color: inherit;
  opacity: 0.7;
  user-select: none;
}

.c-custom-link__text {
  transition: theme('transitionDuration.goat') theme('transitionTiming.ease-in-out');
  transition-property: all;
  cursor: pointer;
  text-decoration: underline transparent;

  &:hover {
    text-decoration-color: var(--btn-primary-hover-type);
    color: var(--btn-primary-hover-type);
  }
}
</style>
